import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import postgresBackendService from '../services/postgresBackendService';
import { useRoleBuilderUpdates } from './useRoleBuilderUpdates';

/**
 * useUserDraftRoles — surfaces role drafts scoped to a specific chat thread,
 * kept in sync with `role_builder_update` WS events.
 *
 * Why a dedicated hook (not just `useRoles({ includeDraft: true }).filter`):
 * the chat banner only needs the slim subset of roles with `hasDraft = true`
 * for the current thread, plus the actions to resolve them. Decoupling it
 * from `useRoles` keeps the sidebar's role list and this banner's list
 * independent, and avoids re-rendering the chat on every role change.
 *
 * Scope: drafts with `draftThreadId === options.threadId` are exposed.
 * Drafts whose `draftThreadId` is null (legacy or manual Builder edits) are
 * NEVER exposed by this hook — they only surface as the sidebar dot.
 * Passing `threadId === null` exposes nothing.
 *
 * Lifecycle:
 *   1. Mount: GET /api/roles?includeDraft=true → cache the hasDraft=true rows
 *      (regardless of thread) in an internal `allDrafts` map.
 *   2. WS `role_builder_update` with `changes.hasDraft = true`:
 *        - if rolId already tracked → update draftUpdatedAt + draftThreadId.
 *        - if rolId unknown → fetch GET /api/roles/:id?includeDraft=true to
 *          resolve name/color/draftThreadId, then add to cache.
 *   3. WS with `changes.hasDraft = false` → remove rolId from cache.
 *   4. WS with `changes.archived = true` → remove rolId from cache.
 *
 * Exposed `drafts` is `allDrafts.filter(d => d.draftThreadId === threadId)`,
 * memoised. Changing threadId in the caller rerenders the consumer with the
 * filtered subset, no refetch.
 *
 * Returned actions wrap postgresBackendService and forward errors to a
 * caller-supplied `onError` (typically a snackbar). State is cleared
 * optimistically; if the backend rejects, the next WS event reconciles.
 *
 * Each draft entry shape: { id, name, color, draftUpdatedAt, draftThreadId }.
 */
export const useUserDraftRoles = ({ threadId = null, onError } = {}) => {
  const { user, loading: authLoading } = useAuth();
  // Cache of every draft the user has, regardless of thread. We filter on
  // exposure so changing `threadId` is a cheap re-derive, not a refetch.
  const [allDrafts, setAllDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingActions, setPendingActions] = useState({}); // { [rolId]: 'publishing' | 'discarding' }

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Fetch initial list of drafts.
  useEffect(() => {
    let cancelled = false;
    if (authLoading) return undefined;
    if (!user?.id) {
      setAllDrafts([]);
      setIsLoading(false);
      return undefined;
    }
    (async () => {
      try {
        setIsLoading(true);
        const all = await postgresBackendService.getAllRoles({ includeDraft: true });
        if (cancelled) return;
        const onlyDrafts = (all || [])
          .filter((r) => r.hasDraft === true)
          .map((r) => ({
            id: r.id,
            name: r.name,
            color: r.color,
            draftUpdatedAt: r.draftUpdatedAt || null,
            draftThreadId: r.draftThreadId || null,
          }));
        setAllDrafts(onlyDrafts);
      } catch (err) {
        console.error('[useUserDraftRoles] initial fetch failed:', err);
        if (!cancelled) setAllDrafts([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id]);

  const upsertDraft = useCallback((rolId, partial) => {
    setAllDrafts((prev) => {
      const idx = prev.findIndex((d) => d.id === rolId);
      if (idx === -1) return [...prev, { id: rolId, ...partial }];
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...partial };
      return next;
    });
  }, []);

  const removeDraft = useCallback((rolId) => {
    setAllDrafts((prev) => prev.filter((d) => d.id !== rolId));
  }, []);

  const resolveAndAdd = useCallback(async (rolId, draftUpdatedAt, draftThreadIdFallback) => {
    try {
      const full = await postgresBackendService.getRolWithDraft(rolId);
      if (!full || full.hasDraft === false) return;
      upsertDraft(rolId, {
        name: full.draftName || full.name,
        color: full.draftColor || full.color,
        draftUpdatedAt: full.draftUpdatedAt || draftUpdatedAt || null,
        draftThreadId: full.draftThreadId || draftThreadIdFallback || null,
      });
    } catch (err) {
      console.error('[useUserDraftRoles] resolve role failed:', err);
    }
  }, [upsertDraft]);

  const handleUpdate = useCallback((rolId, changes) => {
    if (!rolId || !changes) return;
    if (changes.hasDraft === false) {
      removeDraft(rolId);
      return;
    }
    if (changes.hasDraft === true) {
      setAllDrafts((prev) => {
        const exists = prev.some((d) => d.id === rolId);
        if (exists) {
          return prev.map((d) =>
            d.id === rolId
              ? {
                  ...d,
                  draftUpdatedAt: changes.draftUpdatedAt || d.draftUpdatedAt,
                  // Propagate threadId from the event only if the event carries it;
                  // never overwrite a known threadId with null (older workflow versions
                  // emit hasDraft updates without draftThreadId).
                  draftThreadId: changes.draftThreadId || d.draftThreadId || null,
                }
              : d
          );
        }
        // Defer the async resolve outside the setState updater.
        Promise.resolve().then(() => resolveAndAdd(rolId, changes.draftUpdatedAt, changes.draftThreadId));
        return prev;
      });
    }
  }, [removeDraft, resolveAndAdd]);

  const handleArchive = useCallback((rolId) => {
    removeDraft(rolId);
  }, [removeDraft]);

  useRoleBuilderUpdates({
    onInsert: handleUpdate,
    onUpdate: handleUpdate,
    onArchive: handleArchive,
  });

  const setActionState = useCallback((rolId, value) => {
    setPendingActions((prev) => {
      if (value == null) {
        const next = { ...prev };
        delete next[rolId];
        return next;
      }
      return { ...prev, [rolId]: value };
    });
  }, []);

  const publish = useCallback(async (rolId) => {
    if (!rolId) return null;
    setActionState(rolId, 'publishing');
    try {
      const result = await postgresBackendService.publishDraft(rolId);
      removeDraft(rolId);
      return result;
    } catch (err) {
      console.error('[useUserDraftRoles] publish failed:', err);
      onErrorRef.current?.(err, { action: 'publish', rolId });
      throw err;
    } finally {
      setActionState(rolId, null);
    }
  }, [removeDraft, setActionState]);

  const discard = useCallback(async (rolId) => {
    if (!rolId) return null;
    setActionState(rolId, 'discarding');
    try {
      const result = await postgresBackendService.discardDraft(rolId);
      removeDraft(rolId);
      return result;
    } catch (err) {
      console.error('[useUserDraftRoles] discard failed:', err);
      onErrorRef.current?.(err, { action: 'discard', rolId });
      throw err;
    } finally {
      setActionState(rolId, null);
    }
  }, [removeDraft, setActionState]);

  const getImpact = useCallback(async (rolId) => {
    if (!rolId) return null;
    try {
      return await postgresBackendService.getDraftImpact(rolId);
    } catch (err) {
      console.error('[useUserDraftRoles] getImpact failed:', err);
      onErrorRef.current?.(err, { action: 'impact', rolId });
      return { removedTools: [], impactedPlans: [] };
    }
  }, []);

  // Expose only drafts whose origin matches the caller's threadId. Drafts
  // with draftThreadId=null (legacy/manual edits) never surface here.
  const drafts = useMemo(() => {
    if (!threadId) return [];
    return allDrafts.filter((d) => d.draftThreadId === threadId);
  }, [allDrafts, threadId]);

  return {
    drafts,
    isLoading,
    pendingActions,
    publish,
    discard,
    getImpact,
  };
};

export default useUserDraftRoles;
